import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CareConneRoomItem } from '@app/models/user.model';
import { CareConneService } from '@app/services/care-conne/care-conne.service';
import { DialogService } from '@app/services/dialog.service';
import { DialogData } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { RoomCategory } from '@app/shared/enum/care-conne.enum';
import { CareConneSenderCreate, CareConneSenderEmpty } from '@app/store/record/record.classes';
import { RecordSelectors } from '@app/store/record/record.selectors';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { Store } from '@ngxs/store';

@Component({
  selector: 'app-slide-up-window',
  templateUrl: './slide-up-window.component.html',
  styleUrls: ['./slide-up-window.component.scss']
})
export class SlideUpWindowComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private cd: ChangeDetectorRef,
    private dialog: DialogService,
    private careConneService: CareConneService
  ) {
  }

  filters: { name: string, isActive: boolean }[] = [];
  form!: FormGroup;
  allRoomData: CareConneRoomItem[] = [];
  filteredRoomData: CareConneRoomItem[] = [];
  isInited = false;

  currentPage = 0;
  searchUser: string = '';

  isMessageOptional: boolean = false;

  get selectedRoomsId() {
    return this.form.get('selectedRoomIds') as FormControl
  }

  get forwardMessage() {
    return this.form.get('message') as FormControl
  }


  get carePass() {
    return this.store.selectSnapshot(SettingSelectors.CareConnePass);
  }

  get filterValue() {
    return Object.values(RoomCategory).filter(x => x != RoomCategory.SELFCHAT);
  }

  get rooms() {
    let _rooms = [...(this.filteredRoomData || [])];
    if (this.searchUser) {
      const keys = this.searchUser.split(/[ \u3000]+/).filter(x => x);
      _rooms = _rooms.filter(x => keys.some(key => x.name.toLowerCase().includes(key.toLocaleLowerCase())));
    }
    return _rooms;
  }

  get isMessageError() {
    return !this.isMessageOptional && this.form.dirty && this.forwardMessage.invalid;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.isInited = true;
      this.cd.detectChanges();
    }, 10);

  }
  ngOnDestroy(): void {
    this.careConneService.onDestroyModal.next();
  }

  ngOnInit(): void {
    this.filters = this.filterValue.map((x) => {
      return { name: x, isActive: true }
    })
    let config = this.store.selectSnapshot(RecordSelectors.currentCareConneSender);
    let currentMessage = config?.message || '';
    this.isMessageOptional = config?.isMessageOptional || false;
    let _messageCondition = this.isMessageOptional ? [] : [Validators.required, Validators.minLength(1)];
    this.form = this.fb.group({
      selectedRoomIds: [[], [Validators.required]],
      message: [(currentMessage || ''), _messageCondition],
    });
    this.route.data.subscribe((res) => {
      const data = res['rooms'] as CareConneRoomItem[];
      if (!data || !data.length) return;
      this.allRoomData = data;
      this.filterRoomCondition();
    })

  }

  resetSearch() {
    this.searchUser = '';
  }

  filterRoomCondition() {
    const filterCondtions = this.filters.filter(x => x.isActive);
    const _rooms = new Set<CareConneRoomItem>();

    const selfchat = this.allRoomData.filter(x => x.type === 3 && x.userId === this.carePass)
    selfchat.forEach(x => _rooms.add(x));
    for (let filter of filterCondtions) {
      if (filter.name === RoomCategory.YOURCHAT) {
        const rooms = this.allRoomData.filter(x => x.type === 3 && x.userId === this.carePass);
        rooms.forEach(x => _rooms.add(x));
      }
      if (filter.name === RoomCategory.FAMILY) {
        const rooms = this.allRoomData.filter(x => x.type === 1 && x.userInfo.some(info => info.roleId === 3));
        rooms.forEach(x => _rooms.add(x));
      }
      if (filter.name === RoomCategory.DAI) {
        const rooms = this.allRoomData.filter(x => x.type === 1 && x.userInfo.some(info => info.roleId === 1));
        rooms.forEach(x => _rooms.add(x));
      }
      if (filter.name === RoomCategory.STAFF) {
        const rooms = this.allRoomData.filter(x => x.type === 1 && x.userInfo.some(info => info.roleId === 2));
        rooms.forEach(x => _rooms.add(x));
      }
      if (filter.name === RoomCategory.EXTERNAL) {
        const rooms = this.allRoomData.filter(x => x.type === 1 && x.userInfo.some(info => info.roleId === 4));
        rooms.forEach(x => _rooms.add(x));
      }
      if (filter.name === RoomCategory.GROUP) {
        const rooms = this.allRoomData.filter(x => [2, 5, 6].includes(x.type));
        rooms.forEach(x => _rooms.add(x));
      }
    }
    this.filteredRoomData = [..._rooms];
  }

  filterToggle(value: { name: string, isActive: boolean }) {
    if (value.name === RoomCategory.YOURCHAT) return;
    value.isActive = !value.isActive;
    this.filterRoomCondition();
  }

  isSelected(room: CareConneRoomItem) {
    const { roomId } = room;
    return this.selectedRoomsId.value.some((x: number) => x === roomId);
  }

  selectRoom(roomId: number) {
    const index = this.selectedRoomsId.value.findIndex((x: number) => x === roomId);
    if (index >= 0) {
      this.selectedRoomsId.value.splice(index, 1);
    } else {
      this.selectedRoomsId.setValue([...this.selectedRoomsId.value, roomId])
    }

  }

  toNextPage() {
    if (this.page1NextCondition()) return;
    // change page animation
    this.currentPage = Math.min(this.currentPage + 1, 1);
  }

  toPrevPage() {
    this.currentPage = Math.max(this.currentPage - 1, 0);
  }


  page1NextCondition(): boolean {
    return this.selectedRoomsId.value?.length == 0;

  }
  page2NextCondition(): boolean {
    return this.forwardMessage.invalid;

  }

  submit() {
    if (!this.isMessageOptional && this.forwardMessage.invalid) {
      this.form.markAsDirty();
      return;
    }
    // confirm value here
    const { selectedRoomIds, message } = this.form.value;
    this.createRoomConfirm({
      roomIds: selectedRoomIds, message
    }).afterClosed().subscribe(async (res: any) => {
      if (res) {
        this.store.dispatch(new CareConneSenderCreate(selectedRoomIds || [], message || ''));
        await this.closeModal(false);
      }
    })
  }

  public createRoomConfirm(data: {
    roomIds: number[];
    message: string;
  }) {

    const presentRooms = this.allRoomData.filter(x => data.roomIds.some(id => x.roomId === id));
    const sendTo = presentRooms.map(x => `<li>${x.name}</li>`).join('');

    const message = `
    <section style="
    display: flex;
    flex-direction: column;
    font-weight: 300;
    gap: 0.5rem;
    font-size: 0.875rem;
    ">
      <p>下記の内容でケアコネに送信します。</p>

      <span>送信先</span>
      <ul style='padding-left: 1rem;max-height: 10rem;overflow: auto;'>
        ${sendTo}
      </ul>

      <span>メッセージ</span>
      <span style="padding-left: 1rem;width: 100%;overflow-wrap: break-word;max-height: 10rem;overflow: auto;">
       ${data.message.toString()}
      </span>
    </section>
    `;

    const confirmData: DialogData = {
      cancelText: 'いいえ',
      excuteText: 'はい',
      content: `${message}`,
      style: {
        'display': 'flex',
        'flexDirection': 'column',
        'gap': '0.5rem',
        'font-weight': '300'
      }

    };
    return this.dialog.confirm(confirmData);



  }


  async closeModal(isReset: boolean = true) {
    this.isInited = false;
    if (isReset) {
      this.store.dispatch(new CareConneSenderEmpty());
    }
    await new Promise(resolve => setTimeout(resolve, 450));
    this.router.navigate(['', { outlets: { modal: null } }], { relativeTo: this.route, skipLocationChange: true, replaceUrl: true });
  }

}
